import { Stream } from '@mi-front/ui';
import AssistantIcon from '@mui/icons-material/Assistant';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import { Box } from '@mui/material';
import { useRef } from 'react';
import { defineMessage } from 'react-intl';
import { STREAM_UI_CONFIG } from '../../consts';
import { LiveTopicsFocus } from '../../features/live-topics';
import { useLiveTopicsEnabledForStation } from '../../features/live-topics/hooks/useLiveTopicsEnabledForStation';
import { useStreamSettingsStore } from '../../stores/stream-screen-settings.store';
import { useFocusRange } from '../../stores/stream-screen.store';
import { usePersonsEnabledForStation } from '../audit/persons/hooks/usePersonsShow';
import { PersonsTrackDensityToggle } from '../audit/persons/track/PersonsTrackDensityToggle';
import { PersonsTrackGroup } from '../audit/persons/track/PersonsTrackGroup';
import { FocusTrackHeader } from './FocusTrackHeader';
import { FocusTracksPlayhead } from './FocusTracksPlayhead';
import { FocusTracksSeparator } from './FocusTracksSeparator';
import { focusTracksContainerSx } from './focus-tracks-container-sx';
import { useFocusTracksSeek } from './useFocusTracksSeek';

const liveTopicsTrackLabel = defineMessage({
  id: 'logger.streamScreen.liveTopics.track.title',
  defaultMessage: 'Live Topics',
});

const personsTrackLabel = defineMessage({
  id: 'logger.persons.track.title',
  defaultMessage: 'Personas',
});

/**
 * Clip track content so wide/sticky pills cannot paint over the next header.
 * Inter-track separation is handled by {@link FocusTracksSeparator}.
 */
function laneSurfaceSx() {
  return {
    overflow: 'hidden',
    position: 'relative',
    zIndex: 0,
  } as const;
}

/**
 * Time-aligned tracks below the focus area: Live Topics, then Personas.
 * Option D shell: bordered container, banded headers with eye toggles, shared
 * playhead, click-to-seek. Collapsing a track hides its lane but keeps the header.
 */
export function FocusTracks() {
  const range = useFocusRange();
  const containerRef = useRef<HTMLDivElement>(null);
  const seek = useFocusTracksSeek(containerRef, range);

  const liveTopicsEnabled = useLiveTopicsEnabledForStation();
  const personsEnabled = usePersonsEnabledForStation();

  const liveTopicsVisible = useStreamSettingsStore((state) => state.audit.liveTopics ?? true);
  const personsVisible = useStreamSettingsStore((state) => state.audit.persons);
  const updateSettings = useStreamSettingsStore((state) => state.updateSettings);

  if (!liveTopicsEnabled && !personsEnabled) {
    return null;
  }

  const showInterTrackSeparator = liveTopicsEnabled && personsEnabled;

  return (
    <Box ref={containerRef} onClick={seek} sx={focusTracksContainerSx}>
      {liveTopicsEnabled && (
        <>
          <FocusTrackHeader
            label={liveTopicsTrackLabel}
            icon={AssistantIcon}
            visible={liveTopicsVisible}
            onToggle={() => updateSettings({ audit: { liveTopics: !liveTopicsVisible } })}
          />
          {liveTopicsVisible && (
            <Box sx={laneSurfaceSx()}>
              <Stream
                showAxis={false}
                height={STREAM_UI_CONFIG.focusTracksHeight}
                range={range}
                renderHtml={() => <LiveTopicsFocus />}
              />
            </Box>
          )}
        </>
      )}
      {showInterTrackSeparator && <FocusTracksSeparator />}
      {personsEnabled && (
        <>
          <FocusTrackHeader
            label={personsTrackLabel}
            icon={PeopleOutlinedIcon}
            visible={personsVisible}
            onToggle={() => updateSettings({ audit: { persons: !personsVisible } })}
            actions={personsVisible ? <PersonsTrackDensityToggle /> : undefined}
          />
          {personsVisible && (
            <Box sx={laneSurfaceSx()}>
              <PersonsTrackGroup />
            </Box>
          )}
        </>
      )}
      <FocusTracksPlayhead range={range} />
    </Box>
  );
}
